import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getPublicSettlement } from '../services/api';
import { formatExpenseCategory } from '../utils/expenseCategories';
import './ReceiptPage.css';

const SettlementReceiptPage = () => {
    const { public_id } = useParams();
    const { t } = useTranslation();
    const location = useLocation();

    const [settlement, setSettlement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Helper to determine text color based on background brightness
    const getContrastingColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        if (hex.length !== 6) return '#000000';

        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastingColor(settlement?.invoice_color || '#ffffff');
    const mutedColor = textColor === '#FFFFFF' ? '#e0e0e0' : '#666666';

    useEffect(() => {
        const fetchSettlement = async () => {
            try {
                const response = await getPublicSettlement(public_id);
                setSettlement(response.data);
            } catch (err) {
                setError(t('settlementsPage.receiptNotFound') || 'Settlement receipt not found');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchSettlement();
    }, [public_id, t]);

    useEffect(() => {
        if (!loading && settlement) {
            const queryParams = new URLSearchParams(location.search);
            if (queryParams.get('print') === 'true') {
                setTimeout(() => {
                    window.print();
                }, 500);
            }
        }
    }, [loading, settlement, location.search]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(amount || 0);
    };

    if (loading) {
        return <div className="receipt-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner animation="border" /></div>;
    }

    if (error) {
        return <div className="receipt-wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Alert variant="danger">{error}</Alert></div>;
    }

    if (!settlement) {
        return null;
    }

    return (
        <div className="receipt-wrapper" style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', padding: '20px 0' }}>
            <div className="receipt-container" style={{ backgroundColor: settlement.invoice_color || '#fff', color: textColor, maxWidth: '600px' }}>
                {/* Header */}
                <div className="text-center mb-4">
                    {settlement.logo_url && (
                        <img src={settlement.logo_url} alt="Logo" className="receipt-logo mb-2" />
                    )}
                    <h4 className="fw-bold">{settlement.business_name || t('invoicePage.yourBusiness')}</h4>
                    {settlement.business_address && <div className="small" style={{ color: textColor }}>{settlement.business_address}</div>}
                    {settlement.business_phone && <div className="small" style={{ color: textColor }}>Tel: {settlement.business_phone}</div>}
                    {settlement.fiscal_id && (
                        <div style={{ fontSize: '0.85rem', color: mutedColor, marginTop: '2px' }}>
                            {settlement.fiscal_id_type ? `${settlement.fiscal_id_type}: ` : ''}{settlement.fiscal_id}
                        </div>
                    )}
                    {settlement.contact_email && <div className="small">{settlement.contact_email}</div>}
                </div>

                {/* Title */}
                <div className="border-top border-bottom py-2 mb-3" style={{ borderColor: mutedColor }}>
                    <h5 className="text-center fw-bold mb-0">{t('settlementsPage.receipt.title') || 'LIQUIDACIÓN DE PROVEEDOR'}</h5>
                    <div className="text-center small" style={{ color: mutedColor }}>#{settlement.settlement_id}</div>
                </div>

                {/* Supplier Info */}
                <div className="mb-3">
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.supplier')}:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>{settlement.supplier_name}</Col>
                    </Row>
                    {settlement.supplier_tax_id && (
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>NIT/CC:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{settlement.supplier_tax_id}</Col>
                        </Row>
                    )}
                    {settlement.supplier_phone && (
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.phone')}:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{settlement.supplier_phone}</Col>
                        </Row>
                    )}
                    <Row className="mb-1">
                        <Col xs={4} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.period')}:</Col>
                        <Col xs={8} className="text-end" style={{ color: textColor }}>
                            {new Date(settlement.period_start).toLocaleDateString('es-CO', { timeZone: 'UTC' })} - {new Date(settlement.period_end).toLocaleDateString('es-CO', { timeZone: 'UTC' })}
                        </Col>
                    </Row>
                </div>

                {/* Amount Summary */}
                <div className="mb-4">
                    <h6 className="fw-bold border-bottom pb-1 mb-2" style={{ color: textColor, borderColor: mutedColor }}>{t('settlementsPage.receipt.summary') || 'Resumen'}</h6>

                    <Row className="mb-1">
                        <Col xs={8} style={{ color: textColor }}>{t('settlementsPage.receipt.grossAmount') || 'Monto Bruto'}:</Col>
                        <Col xs={4} className="text-end" style={{ color: textColor }}>{formatCurrency(settlement.gross_amount)}</Col>
                    </Row>

                    {/* Deductions */}
                    {settlement.deducted_expenses && settlement.deducted_expenses.length > 0 && (
                        <>
                            <div className="mt-3 mb-2 small fw-bold" style={{ color: mutedColor }}>{t('settlementsPage.receipt.deductions') || 'Deducciones'}:</div>
                            {settlement.deducted_expenses.map((expense, index) => (
                                <Row key={expense.expense_id || index} className="mb-1 small">
                                    <Col xs={2} style={{ color: mutedColor }}>{new Date(expense.date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</Col>
                                    <Col xs={6} style={{ color: mutedColor }}>{expense.description || formatExpenseCategory(expense.category, t)}</Col>
                                    <Col xs={4} className="text-end text-danger">-{formatCurrency(expense.amount)}</Col>
                                </Row>
                            ))}
                        </>
                    )}

                    <Row className="mb-1 mt-2">
                        <Col xs={8} style={{ color: textColor }}>{t('settlementsPage.receipt.totalDeductions') || 'Total Deducciones'}:</Col>
                        <Col xs={4} className="text-end text-danger fw-bold">-{formatCurrency(settlement.total_deductions)}</Col>
                    </Row>

                    <div className="border-top pt-2 mt-2" style={{ borderColor: mutedColor }}>
                        <Row>
                            <Col xs={8} className="fw-bold" style={{ color: textColor, fontSize: '1.1rem' }}>{t('settlementsPage.receipt.netAmount') || 'NETO A PAGAR'}:</Col>
                            <Col xs={4} className="text-end fw-bold" style={{ color: textColor, fontSize: '1.2rem' }}>{formatCurrency(settlement.net_amount)}</Col>
                        </Row>
                    </div>
                </div>

                {/* Status */}
                <div className="border-top pt-2 mb-4" style={{ borderColor: mutedColor }}>
                    <Row className="mb-1">
                        <Col xs={5} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.status')}:</Col>
                        <Col xs={7} className="text-end">
                            {settlement.status === 'PAID' ? (
                                <span className="badge bg-success">{t('settlementsPage.status.PAID')}</span>
                            ) : (
                                <span className="badge bg-warning text-dark">{t('settlementsPage.status.PENDING')}</span>
                            )}
                        </Col>
                    </Row>
                    {settlement.status === 'PAID' && settlement.payment_date && (
                        <Row className="mb-1">
                            <Col xs={5} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.paymentDate')}:</Col>
                            <Col xs={7} className="text-end" style={{ color: textColor }}>{new Date(settlement.payment_date).toLocaleDateString('es-CO', { timeZone: 'UTC' })}</Col>
                        </Row>
                    )}
                    {settlement.payment_method && (
                        <Row className="mb-1">
                            <Col xs={5} className="fw-bold" style={{ color: textColor }}>{t('settlementsPage.receipt.paymentMethod')}:</Col>
                            <Col xs={7} className="text-end" style={{ color: textColor }}>{t(`expensesPage.paymentMethods.${settlement.payment_method}`) || settlement.payment_method}</Col>
                        </Row>
                    )}
                </div>

                {/* Notes */}
                {settlement.notes && (
                    <div className="mb-4" style={{ backgroundColor: textColor === '#FFFFFF' ? 'rgba(255,255,255,0.1)' : '#f9f9f9', padding: '10px', borderRadius: '5px' }}>
                        <small className="fw-bold">{t('settlementsPage.receipt.notes')}:</small>
                        <div className="small" style={{ color: mutedColor }}>{settlement.notes}</div>
                    </div>
                )}

                {/* Footer */}
                <div className="receipt-footer text-center mt-4 pt-3 border-top">
                    {/* QR Code */}
                    {settlement.qr_code_url && (
                        <div className="receipt-qr-code text-center mb-3">
                            <img
                                src={settlement.qr_code_url}
                                alt="QR Code"
                                style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                            />
                        </div>
                    )}

                    <div className="small" style={{ color: mutedColor }}>{t('settlementsPage.receipt.thankYou') || 'Gracias por su colaboración.'}</div>
                    <div className="powered-by mt-2">
                        <small style={{ fontSize: '0.7rem', color: mutedColor }}>Powered by Evazard</small>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettlementReceiptPage;
